'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import {
  UiKitPaginationBase,
  UiKitPaginationBaseProps
} from '@/fo/ui/kit/pagination/base';
import type { CatalogTheme } from '@/fo/util/theme';

import { CategoriesTagsList } from '../components/CategoriesTagsList/CategoriesTagsList';
import { FiltersPanel } from '../components/FiltersPanel/FiltersPanel';
import { SortBlock } from '../components/SortBlock/SortBlock';
import { DEFAULT_SORT_OPTION, SORT_OPTIONS } from '../constants';
import {
  FilterChangeArgs,
  FilterKeys,
  FilterOption,
  Filters,
  SelectedFilters,
  TopCategories
} from '../types';
import { handleMainFilterChange } from '../util/catalog-filters';
import { isEmptyObject } from '../util/empty-object';

import { styles } from './UiProductLayoutCatalog.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

/** Компонент каталога продуктов с фильтрами, пагинацией и SEO-блоками */
export interface UiProductLayoutCatalogProps {
  /** Контент каталога (список продуктов) */
  children?: React.ReactNode;
  /** Обработчик клика */
  onClick?: NativeElement['onClick'];
  /** Параметры пагинации */
  pagination: {
    count: UiKitPaginationBaseProps['count'];
    page: UiKitPaginationBaseProps['page'];
  };
  /** SEO-блок в верхней части каталога */
  topSeoBlock: React.ReactNode;
  /** SEO-блок в нижней части каталога */
  bottomSeoBlock?: React.ReactNode;
  /** Фильтры в верхней части каталога (теги)*/
  topCategories?: TopCategories[];
  /** Конфигурация фильтров */
  filters: Filters;
  /** Выбранные значения фильтров */
  selectedFilters: SelectedFilters;
  /** Выбранные значения сортировки */
  selectedSort: string;
  /** Обработчик изменения фильтров */
  onFiltersChange?: (args: FilterChangeArgs, withUpdate?: boolean) => void;
  /** Количество продуктов, которые будут отображены в каталоге */
  productsCount?: number;
  /** Обработчик отображения продуктов */
  onShowProducts?: () => void;
  /** Обработчик изменения открытия фильтров */
  onOpenChange?: (open: boolean) => void;
  /** Обработчик сброса фильтров */
  onReset?: (key: FilterKeys) => void;
  /** Обработчик изменения сортировки */
  onSortChange?: (option: FilterOption | null) => void;
  /** Обработчик изменения пагинации */
  setPagination?: (pagination: { count: number; page: number }) => void;
  /** Флаг загрузки */
  isLoading?: boolean;
  /** Обработчик загрузки большего количества продуктов */
  onLoadMore?: () => void;
  /** Флаг открытия модального окна */
  isOpenModal?: boolean;
  /** Тема оформления каталога */
  theme?: CatalogTheme;
}

export function UiProductLayoutCatalog({
  children,
  topSeoBlock,
  pagination,
  bottomSeoBlock,
  topCategories = [],
  filters,
  selectedFilters,
  selectedSort = DEFAULT_SORT_OPTION.value,
  onFiltersChange = () => {},
  productsCount = 0,
  onShowProducts = () => {},
  onOpenChange = () => {},
  onReset = () => {},
  onSortChange = () => {},
  setPagination = () => {},
  isLoading = false,
  onLoadMore = () => {},
  isOpenModal = false,
  theme = 'default'
}: UiProductLayoutCatalogProps) {
  const t = useTranslations();
  const slots = styles({ theme });

  const { count = 0, page = 1 } = pagination;

  const priceFilterIndex = filters.mainFilters.findIndex((filter) => {
    if (!filter) return false;
    return filter.slug === 'price';
  });

  const priceFilter = filters.mainFilters.splice(priceFilterIndex, 1)[0];
  filters.mainFilters.unshift(priceFilter);

  const handleMainFilterChangeLocal = (
    slug: string,
    value: string | [number, number],
    checked: boolean,
    withUpdate?: boolean
  ) => {
    handleMainFilterChange(
      slug,
      value,
      checked,
      selectedFilters,
      onFiltersChange,
      withUpdate
    );
  };
  const handleShowProducts = () => onShowProducts?.();

  return (
    <div>
      <CategoriesTagsList
        className={slots.careTagsList()}
        tagsList={topCategories}
        onFilterChange={handleMainFilterChangeLocal}
        selectedFilters={selectedFilters}
        productsCount={productsCount}
        isOpenModal={isOpenModal}
        isLoading={isLoading}
      />
      {Boolean(topSeoBlock) && (
        <div className={slots.gammaSeoBlock()}>{topSeoBlock}</div>
      )}
      <div className={slots.filters()}>
        <FiltersPanel
          theme={theme}
          filters={filters.mainFilters}
          selectedFilters={selectedFilters}
          onFilterChange={handleMainFilterChangeLocal}
          onReset={() => onReset?.(FilterKeys.mainFilters)}
          onOpenChange={onOpenChange}
          onShowProducts={handleShowProducts}
          productsCount={productsCount}
          isLoading={isLoading}
          isOpenModal={isOpenModal}
        />
        <SortBlock
          theme={theme}
          className={slots.sortSelect()}
          sortByFilters={SORT_OPTIONS(t)}
          selectedSortByFilters={selectedSort || DEFAULT_SORT_OPTION.value}
          onSortChange={onSortChange}
        />
      </div>

      {count > 0 ? (
        <div className={slots.productWrapper()}>{children}</div>
      ) : isEmptyObject(selectedFilters) ? (
        <div className={slots.notFound()}>
          {t('productCatalog.emptyCategory')}
        </div>
      ) : (
        <div className={slots.notFound()}>{t('productCatalog.notFound')}</div>
      )}
      <div className={slots.pagination()}>
        {count > 1 && (
          <UiKitPaginationBase
            theme={theme}
            count={count}
            page={page}
            onChange={(page) => setPagination({ count, page })}
            disabledNavigation
            onLoadMore={onLoadMore}
          />
        )}
      </div>

      {Boolean(bottomSeoBlock) && (
        <div className={slots.bottomSeoBlock()}>{bottomSeoBlock}</div>
      )}
    </div>
  );
}
